import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import MapboxMap, {
  FullscreenControl,
  GeolocateControl,
  Layer,
  NavigationControl,
  Source,
} from "react-map-gl/mapbox";
import mapboxgl from "mapbox-gl";
import { Box, FormControlLabel, Switch, ToggleButton, ToggleButtonGroup } from "@mui/material";

import useSidebarWidth from "../../shared/hooks/useSidebarWidth";
import SideMenu from "./sideMenu";
import { MarkerHover } from "./MarkerPopup";
import { addMarkerIcons } from "./markerIcons";
import {
  ACCIDENT_ICON_LAYER_ID,
  ACCIDENT_LABEL_LAYER_ID,
  ACCIDENT_POINT_LAYER_ID,
  ACCIDENT_SOURCE_ID,
  CLUSTER_COUNT_LAYER_ID,
  CLUSTER_LAYER_ID,
  EMPTY_FILTERS,
  INITIAL_VIEW,
  ICON_LAYER_ID,
  LABEL_LAYER_ID,
  LAYERS,
  MAP_STYLE,
  POINT_LAYER_ID,
  SIDEBAR_MIN_WIDTH,
  SIDEBAR_STORAGE_KEY,
  SOURCE_ID,
} from "./constants";
import { entitiesForLayer } from "./portfolioData";
import {
  accidentFeatures,
  matchesQueryAndFilters,
  selectionFeatures,
  toFeature,
  toFeatureCollection,
} from "./helpers";
import styles from "./portfolioExplorer.module.css";

import "mapbox-gl/dist/mapbox-gl.css";

function PortfolioExplorer() {
  const { t } = useTranslation();
  const mapRef = useRef(null);
  const { width: drawerWidth, setWidth: setDrawerWidth } = useSidebarWidth({
    storageKey: SIDEBAR_STORAGE_KEY,
    minWidth: SIDEBAR_MIN_WIDTH,
  });
  const [menuOpen, setMenuOpen] = useState(true);
  const [layer, setLayer] = useState(LAYERS.broker);
  const [stack, setStack] = useState([]);
  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [order, setOrder] = useState("created:desc");
  const [showAccidents, setShowAccidents] = useState(false);
  const hoverRef = useRef(null);
  const hoverTimer = useRef(0);
  const shownHoverId = useRef(null);
  const pendingHoverId = useRef(null);

  const selection = stack[stack.length - 1] || null;

  const listFeatures = useMemo(() => {
    const matched = entitiesForLayer[layer].filter((entity) =>
      matchesQueryAndFilters(entity, layer, query, filters),
    );
    return matched.map((entity) => toFeature(entity, layer));
  }, [layer, query, filters]);

  const accidentsVisible = layer === LAYERS.motor && showAccidents;
  const accidentGeojson = useMemo(() => {
    if (!accidentsVisible) return toFeatureCollection([]);
    const motorIds = new Set(listFeatures.map((feature) => feature.properties.entityId));
    return toFeatureCollection(accidentFeatures(motorIds));
  }, [accidentsVisible, listFeatures]);

  const geojson = useMemo(() => {
    if (!selection || selection.type === LAYERS.broker) {
      return toFeatureCollection(
        selection ? selectionFeatures(selection) : listFeatures,
      );
    }

    const previous = stack[stack.length - 2];
    if (previous?.type === LAYERS.broker) {
      return toFeatureCollection(selectionFeatures(previous));
    }

    const brokerFeature = selectionFeatures(selection).find(
      (feature) => feature.properties.entityType === LAYERS.broker,
    );
    const visible = brokerFeature ? [...listFeatures, brokerFeature] : listFeatures;
    return toFeatureCollection(visible);
  }, [selection, listFeatures, stack]);

  const openEntity = (type, id) => {
    setStack((current) => {
      const top = current[current.length - 1];
      if (top && top.type === type && top.id === id) return current;
      return [...current, { type, id }];
    });
  };

  const handleLayerChange = (_, value) => {
    if (!value) return;
    setLayer(value);
    setStack([]);
    setQuery("");
    setFilters(EMPTY_FILTERS);
    setOrder("created:desc");
    setShowAccidents(false);
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      mapRef.current?.getMap()?.resize();
    }, 300);
    return () => clearTimeout(timeout);
  }, [menuOpen, drawerWidth]);

  useEffect(() => {
    if (!selection) return undefined;
    const map = mapRef.current?.getMap();
    if (!map) return undefined;

    const moveToSelection = () => {
      const features = selectionFeatures(selection);
      if (!features.length) return;

      if (selection.type !== LAYERS.broker) {
        const selectedFeature = features.find(
          (feature) => feature.properties.entityId === selection.id,
        );
        if (!selectedFeature) return;
        map.flyTo({
          center: selectedFeature.geometry.coordinates,
          zoom: Math.max(map.getZoom(), 14),
          duration: 800,
        });
        return;
      }

      const bounds = new mapboxgl.LngLatBounds();
      features.forEach((feature) =>
        bounds.extend(feature.geometry.coordinates),
      );
      map.fitBounds(bounds, { padding: 80, maxZoom: 14, duration: 800 });
    };

    if (map.isStyleLoaded()) {
      moveToSelection();
      return undefined;
    }

    map.once("idle", moveToSelection);
    return () => map.off("idle", moveToSelection);
  }, [selection]);

  const handleMapClick = (event) => {
    const feature = event.features?.[0];
    if (!feature) return;

    const isCluster =
      feature.properties.cluster === true ||
      feature.properties.cluster === "true";
    if (isCluster) {
      const map = mapRef.current.getMap();
      const source = map.getSource(SOURCE_ID);
      const [longitude, latitude] = feature.geometry.coordinates;
      source.getClusterExpansionZoom(
        Number(feature.properties.cluster_id),
        (error, zoom) => {
          if (error) return;
          map.easeTo({ center: [longitude, latitude], zoom });
        },
      );
      return;
    }

    const entityType = feature.properties.entityType === "accident"
      ? LAYERS.motor
      : feature.properties.entityType;
    openEntity(entityType, feature.properties.entityId);
  };

  const clearHoverTimer = () => {
    if (!hoverTimer.current) return;
    clearTimeout(hoverTimer.current);
    hoverTimer.current = 0;
  };

  const handleMouseMove = (event) => {
    const feature = event.features?.find(
      (item) =>
        item.properties?.entityId &&
        item.properties.cluster !== true &&
        item.properties.cluster !== "true",
    );
    if (!feature) {
      clearHoverTimer();
      shownHoverId.current = null;
      pendingHoverId.current = null;
      hoverRef.current?.(null);
      return;
    }

    const next = {
      x: event.point.x,
      y: event.point.y,
      entityId: feature.properties.entityId,
      entityType: feature.properties.entityType,
    };

    if (shownHoverId.current === next.entityId) {
      hoverRef.current?.(next);
      return;
    }

    if (pendingHoverId.current === next.entityId) return;

    clearHoverTimer();
    shownHoverId.current = null;
    hoverRef.current?.(null);
    pendingHoverId.current = next.entityId;
    hoverTimer.current = window.setTimeout(() => {
      hoverTimer.current = 0;
      pendingHoverId.current = null;
      shownHoverId.current = next.entityId;
      hoverRef.current?.(next);
    }, 200);
  };

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "relative",
          flexGrow: 1,
          height: "calc(100vh - 64px)",
          width: { sm: `calc(100% - ${menuOpen ? drawerWidth : 0}px)` },
          marginRight: menuOpen ? `${drawerWidth}px` : 0,
          transition: "margin 0.3s",
        }}
      >
        <MapboxMap
          ref={mapRef}
          initialViewState={INITIAL_VIEW}
          mapStyle={MAP_STYLE.street}
          mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
          style={{ width: "100%", height: "100%" }}
          interactiveLayerIds={[
            CLUSTER_LAYER_ID,
            POINT_LAYER_ID,
            LABEL_LAYER_ID,
            ...(accidentsVisible
              ? [ACCIDENT_POINT_LAYER_ID, ACCIDENT_ICON_LAYER_ID, ACCIDENT_LABEL_LAYER_ID]
              : []),
          ]}
          onLoad={(event) => {
            const map = event.target;
            const keepRoadsOnly = () => {
              addMarkerIcons(map);
              [
                "showPointOfInterestLabels",
                "showTransitLabels",
                "showPlaceLabels",
                "showLandmarkIcons",
                "showLandmarkIconLabels",
              ].forEach((property) => {
                try {
                  map.setConfigProperty("basemap", property, false);
                } catch (error) {
                  // Older Standard styles do not expose every config key.
                }
              });
            };
            keepRoadsOnly();
            map.on("style.load", keepRoadsOnly);
          }}
          onClick={handleMapClick}
          onMouseMove={handleMouseMove}
          onMouseLeave={() => {
            clearHoverTimer();
            shownHoverId.current = null;
            pendingHoverId.current = null;
            hoverRef.current?.(null);
          }}
          cursor="pointer"
        >
          <NavigationControl position="top-right" />
          <GeolocateControl position="top-right" />
          <FullscreenControl position="top-right" />
          <Source
            id={SOURCE_ID}
            type="geojson"
            data={geojson}
            cluster
            clusterRadius={50}
            clusterMaxZoom={12}
          >
            <Layer
              id={CLUSTER_LAYER_ID}
              slot="top"
              type="circle"
              filter={["has", "point_count"]}
              paint={{
                "circle-color": "#455A64",
                "circle-radius": [
                  "step",
                  ["get", "point_count"],
                  16,
                  10,
                  20,
                  30,
                  26,
                ],
                "circle-stroke-width": 2,
                "circle-stroke-color": "#ffffff",
              }}
            />
            <Layer
              id={CLUSTER_COUNT_LAYER_ID}
              slot="top"
              type="symbol"
              filter={["has", "point_count"]}
              layout={{
                "text-field": ["get", "point_count_abbreviated"],
                "text-size": 12,
              }}
              paint={{ "text-color": "#ffffff" }}
            />
            <Layer
              id={POINT_LAYER_ID}
              slot="top"
              type="circle"
              filter={["!", ["has", "point_count"]]}
              paint={{
                "circle-color": ["get", "color"],
                "circle-radius": ["case", ["==", ["get", "icon"], ""], 8, 14],
                "circle-stroke-width": 2,
                "circle-stroke-color": "#ffffff",
              }}
            />
            <Layer
              id={ICON_LAYER_ID}
              slot="top"
              type="symbol"
              filter={[
                "all",
                ["!", ["has", "point_count"]],
                ["!=", ["get", "icon"], ""],
              ]}
              layout={{
                "icon-image": ["get", "icon"],
                "icon-size": 0.45,
                "icon-allow-overlap": true,
                "icon-ignore-placement": true,
              }}
            />
            <Layer
              id={LABEL_LAYER_ID}
              slot="top"
              type="symbol"
              minzoom={13}
              filter={["!", ["has", "point_count"]]}
              layout={{
                "text-field": ["get", "label"],
                "text-font": ["DIN Pro Bold", "Arial Unicode MS Bold"],
                "text-size": 14,
                "text-offset": [0, -1.35],
                "text-anchor": "bottom",
                "text-max-width": 14,
              }}
              paint={{
                "text-color": ["get", "color"],
                "text-halo-color": "#ffffff",
                "text-halo-width": 2,
              }}
            />
          </Source>
          {accidentsVisible && (
            <Source id={ACCIDENT_SOURCE_ID} type="geojson" data={accidentGeojson}>
              <Layer
                id={ACCIDENT_POINT_LAYER_ID}
                slot="top"
                type="circle"
                paint={{
                  "circle-color": ["get", "color"],
                  "circle-radius": 14,
                  "circle-stroke-width": 2,
                  "circle-stroke-color": "#ffffff",
                }}
              />
              <Layer
                id={ACCIDENT_ICON_LAYER_ID}
                slot="top"
                type="symbol"
                layout={{
                  "icon-image": ["get", "icon"],
                  "icon-size": 0.45,
                  "icon-allow-overlap": true,
                  "icon-ignore-placement": true,
                }}
              />
              <Layer
                id={ACCIDENT_LABEL_LAYER_ID}
                slot="top"
                type="symbol"
                minzoom={13}
                layout={{
                  "text-field": ["get", "label"],
                  "text-font": ["DIN Pro Bold", "Arial Unicode MS Bold"],
                  "text-size": 14,
                  "text-offset": [0, -1.35],
                  "text-anchor": "bottom",
                  "text-max-width": 14,
                }}
                paint={{
                  "text-color": ["get", "color"],
                  "text-halo-color": "#ffffff",
                  "text-halo-width": 2,
                }}
              />
            </Source>
          )}
        </MapboxMap>
        <MarkerHover hoverRef={hoverRef} />

        <ToggleButtonGroup
          className={styles.layerToggle}
          exclusive
          size="small"
          color="primary"
          value={layer}
          onChange={handleLayerChange}
        >
          <ToggleButton value={LAYERS.broker}>
            {t("portfolioExplorer.layers.brokers")}
          </ToggleButton>
          <ToggleButton value={LAYERS.property}>
            {t("portfolioExplorer.layers.properties")}
          </ToggleButton>
          <ToggleButton value={LAYERS.motor}>
            {t("portfolioExplorer.layers.motors")}
          </ToggleButton>
        </ToggleButtonGroup>
        {layer === LAYERS.motor && (
          <FormControlLabel
            className={styles.accidentToggle}
            label={t("portfolioExplorer.accidents")}
            control={(
              <Switch
                size="small"
                checked={showAccidents}
                onChange={(event) => setShowAccidents(event.target.checked)}
              />
            )}
          />
        )}
      </Box>

      <SideMenu
        open={menuOpen}
        onToggle={() => setMenuOpen((current) => !current)}
        width={drawerWidth}
        onResize={setDrawerWidth}
        layer={layer}
        stack={stack}
        onOpen={openEntity}
        onBack={() => setStack((current) => current.slice(0, -1))}
        query={query}
        onQueryChange={setQuery}
        filters={filters}
        onFiltersChange={setFilters}
        order={order}
        onOrderChange={setOrder}
      />
    </Box>
  );
}

export default PortfolioExplorer;
